import handleTransaction from "helpers/handleTransaction";
import { getTokenByName } from "./../eth/contracts/contracts";
import { formatResult } from "helpers/utils";
import { getContractByName } from "eth/contracts/contracts";
import EthStore from "stores/EthStore";
import { Contract } from "ethers";
import { action, observable, runInAction } from "mobx";
import { FarmingPool, FarmingPoolData, Token } from "types";
import { formatUnits, parseEther } from "ethers/lib/utils";
import { getYieldLP } from "helpers/money";

class PoolStore {
  @observable poolData: Map<string, FarmingPoolData> = new Map();

  @action fetchPoolData = async (pool: FarmingPool) => {
    const multiplierContract = getContractByName("Multiplier", EthStore.network);
    const stakingTokenContract = getTokenByName(pool.stakingToken.name, EthStore.network);
    const multiplierTokenContracts = pool.multiplierTokens.map((token) => new Contract(token.address, token.abi, EthStore.signer));

    const contract = new Contract(pool.address, pool.abi, EthStore.signer);
    const multiplier = new Contract(multiplierContract.address, multiplierContract.abi, EthStore.provider);
    const stakingToken = new Contract(stakingTokenContract.address, stakingTokenContract.abi, EthStore.signer);

    const currentLevels = await Promise.all(
      pool.multiplierTokens.map(async (token) => {
        console.log(token);
        return Number(await multiplier.getLastTokenLevelForUser(pool.address, EthStore.account, token.address));
      })
    );

    const boostCosts = await Promise.all(
      pool.multiplierTokens.map(
        async (token, index) =>
          await Promise.all(
            [1, 2, 3, 4].map(async (level) =>
              level > currentLevels[index]
                ? formatUnits(await contract.calculateCost(pool.address, token.address, level), token.decimals)
                : 0
            )
          )
      )
    );
    const rewardPerToken = Number(await contract.rewardPerToken());
    const rewardRate = Number(await contract.rewardRate());

    const data: FarmingPoolData = {
      rewardPerToken,
      rewardRate,
      boostCosts,
      currentLevels,
      multiplierAllowances: await Promise.all(
        multiplierTokenContracts.map(async (contract) => formatResult(await contract.allowance(EthStore.account, pool.address)) > 100)
      ),
      hasStakingAllowance: formatResult(await stakingToken.allowance(EthStore.account, pool.address)) > 10,
      yields: await getYieldLP(pool, rewardRate, rewardPerToken, EthStore.provider),
      totalSupply: formatResult(await contract.totalSupply(), pool.stakingToken.decimals),
      duration: Number(await contract.DURATION()),
      earned: formatResult(await contract.earned(EthStore.account), pool.rewardToken.decimals),
      stake: formatResult(await contract.balanceOf(EthStore.account), pool.stakingToken.decimals),
      bonusStake: formatResult(await contract._balancesAccounting(EthStore.account), pool.stakingToken.decimals),
      multiplier: Number(await contract.getTotalMultiplier(EthStore.account)) / 10,
      periodFinish: Number(await contract.periodFinish()),
    };

    runInAction(() => this.poolData.set(pool.name, data));
  };

  @action claim = async (pool: FarmingPool) => {
    const contract = new Contract(pool.address, pool.abi, EthStore.signer);
    await handleTransaction(async () => await contract.getReward(), `reward claim`);
    await this.fetchPoolData(pool);
  };
  @action withdraw = async (pool: FarmingPool, amount: number) => {
    const contract = new Contract(pool.address, pool.abi, EthStore.signer);
    await handleTransaction(async () => await contract.withdraw(parseEther(amount.toString())), `withdraw`);
    await this.fetchPoolData(pool);
  };
  @action stake = async (pool: FarmingPool, amount: number) => {
    const contract = new Contract(pool.address, pool.abi, EthStore.signer);
    const parsedAmount = parseEther(amount.toString());
    await handleTransaction(async () => await contract.stake(parsedAmount), `stake`);
    await this.fetchPoolData(pool);
  };

  @action boost = async (pool: FarmingPool, token: Token, level: number) => {
    const contract = new Contract(pool.address, pool.abi, EthStore.signer);
    await handleTransaction(async () => await contract.purchase(token.address, level), `stake`);
    await this.fetchPoolData(pool);
  };

  getAllowance = async (pool: FarmingPool, token: Token) => {
    const contract = new Contract(token.address, token.abi, EthStore.signer);
    await handleTransaction(async () => await contract.approve(pool.address, parseEther("100000000")), `approval`);
    await this.fetchPoolData(pool);
  };
}

export default new PoolStore();
